/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type BaseRequestOptionsArgs, type HttpResponse, HttpService } from '@dm/core';
import { type Rest } from '@dm/core/models/rest';
import { MO } from 'app/common/constants';
import { TaskService, ToolService } from 'app/common/services';
import { type Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class SmartCacheService {
    constructor(
        private httpService: HttpService,
        private toolService: ToolService,
        private taskService: TaskService,
    ) { }

    /**
     * 查询disk信息
     */
    getAllEngineDiskInfo(
        id?: number | string | BaseRequestOptionsArgs,
        oriOptions?: BaseRequestOptionsArgs,
    ): Observable<HttpResponse> {
        let options = oriOptions;
        const rest = this.httpService.makeUrl('smartCachePool.getAllEngineDiskinfo');
        if (typeof id === 'object') {
            options = id;
        } else {
            rest.setParams({ id, type: MO.SMARTCACHE_POOL_ENGINE });
        }
        return this.httpService.get(rest, options);
    }

    /**
     * 批量查控制框信息
     */
    getEnclosureBatch(params?, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('enclosure.getBatch');
        if (params) {
            rest.setParams(params);
        }
        return this.httpService.get(rest, options);
    }

    /**
     * 批量查询硬盘硬盘
     */
    getDisksBatch(params?, options?: BaseRequestOptionsArgs): Observable<Disk[]> {
        const rest = this.httpService.makeUrl('disk.getBatch');
        if (params) {
            rest.setParams(params);
        }
        return this.toolService.getAllData(rest, options);
    }

    /**
     * 批量查询硬盘rest
     */
    getDisksBatchRest(params?): Rest {
        const rest = this.httpService.makeUrl('disk.getBatch');
        if (params) {
            rest.setParams(params);
        }
        return rest;
    }

    /**
     * 查询硬盘域的硬盘
     */
    getDisks(diskDomainId: number | string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('disk.getBatch');
        rest.setParams({ POOLID: diskDomainId });
        return this.httpService.get(rest, options);
    }

    /**
     * 添加盘（扩容）
     */
    addDisk(body, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('smartCachePool.addDisk');
        return this.httpService.put(rest, body, options);
    }

    /**
     * 创建缓存池
     */
    create(objName: string, params: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('smartcache.create', 'V2');
        return this.taskService.post('createSmartcachePool', objName, rest, params, options);
    }

    /**
     * 获取批量查询缓存池的rest
     */
    getBatchRest(options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('smartCachePool.getBatch');
        return this.httpService.get(rest, options);
    }

    /**
     * 查询缓存池详情
     */
    getSmartCachePool(options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('smartCachePool.get');
        return this.httpService.get(rest, options);
    }

    /**
     * 修改智能缓存池
     */
    modifySmartcachePool(body, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('smartCachePool.modify');
        return this.httpService.put(rest, body, options);
    }

    /**
     * 修改智能缓存池的开关
     */
    modifySmartcachePoolSwitch(body, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('smartCachePool.modify');
        return this.httpService.put(rest, body, options);
    }

    /**
     * 修改智能缓存池的描述
     */
    modifySmartcachePoolDesctiption(body, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('smartCachePool.modify');
        return this.httpService.put(rest, body, options);
    }

    /**
     * 删除智能缓存池
     */
    deleteSmartCachePool(poolId, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('smartCachePool.deleteSmartCachePool').format(poolId);
        return this.httpService.delete(rest, options);
    }

    /**
     * 关联查询智能缓存分区的信息
     */
    getAssociatePartition(params, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('smartCachePool.getAssociatePartition');
        rest.setParams(params);
        return this.httpService.get(rest, options);
    }

    /**
     * 查询智能缓存分区的信息
     */
    getPartition(params, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('smartCachePool.getPartition');
        rest.setParams(params);
        return this.httpService.get(rest, options);
    }

    /**
     * 获取可添加LUN的rest,通过SMARTCACHEPARTITIONID为'--'过滤已加入LUN
     */
    getLunRest(): Rest {
        return this.httpService.makeUrl('lun.getLUN').setFilters([
            { field: 'SUBTYPE', value: '0', matchMode: '::' },
            { field: 'SMARTCACHEPARTITIONID', value: '--', matchMode: '::' },
            // 排除容器LUN
            { field: 'USAGETYPE', value: '5_6_7', matchMode: '!!' },
        ]);
    }

    /**
     * 查询缓存池中LUN的rest
     */
    getSmartCachePartitionLunRest(partitionId: string): Rest {
        const rest = this.httpService.makeUrl('smartCachePool.getAsscociateLun');
        rest.setParams({
            ASSOCIATEOBJTYPE: MO.SMARTCACHE_PARTITION,
            ASSOCIATEOBJID: partitionId,
        });
        return rest;
    }

    /**
     * 查询缓存池中LUN的rest
     */
    getSmartCachePartitionLunCountRest(partitionId: string): Rest {
        const rest = this.httpService.makeUrl('smartCachePool.getAsscociateLunCount');
        rest.setParams({
            ASSOCIATEOBJTYPE: MO.SMARTCACHE_PARTITION,
            ASSOCIATEOBJID: partitionId,
        });
        return rest;
    }

    /**
     * 获取可添加文件系统的rest，通过ASSOCIATEOBJTYPE为273过滤已加入smartcache的文件系统
     */
    getFileSystemRest(): Rest {
        const rest = this.httpService.makeUrl('fileSystem.getAssociate', undefined, true);
        rest.setParams({
            vstoreId: -1,
            ASSOCIATEOBJTYPE: MO.SMARTCACHE_PARTITION,
            ASSOCIATEOBJID: -1,
        });
        return rest;
    }

    /**
     * 查询缓存池中FS的rest
     */
    getSmartCachePartitionFsRest(partitionId: string): Rest {
        const rest = this.httpService.makeUrl('smartCachePool.getAsscociateFs');
        rest.setParams({
            ASSOCIATEOBJTYPE: MO.SMARTCACHE_PARTITION,
            ASSOCIATEOBJID: partitionId,
        });
        return rest;
    }

    /**
     * 查询缓存池中FS的rest
     */
    getSmartCachePartitionFsCountRest(partitionId: string): Rest {
        const rest = this.httpService.makeUrl('smartCachePool.getAsscociateFsCount');
        rest.setParams({
            ASSOCIATEOBJTYPE: MO.SMARTCACHE_PARTITION,
            ASSOCIATEOBJID: partitionId,
        });
        return rest;
    }

    /**
     * 添加关联（Members）
     */
    addAsscociate(
        body: { id: string; associateObjType: any; associateObjID: string },
        options?: BaseRequestOptionsArgs,
    ): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('smartCachePool.addAsscociate');
        return this.httpService.put(rest, body, options);
    }

    /**
     * 删除关联（Members）
     */
    removeAsscociate(
        body: {
            id: string;
            associateObjType: any; associateObjID: string;
        },
        options?: BaseRequestOptionsArgs,
    ): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('smartCachePool.removeAsscociate');
        return this.httpService.put(rest, body, options);
    }

    /**
     * 修改智能缓存分区
     */
    modifyPartition(body: { id: string; name: string }, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('smartCachePool.modifyPartition');
        return this.httpService.put(rest, body, options);
    }

    /**
     * 删除智能缓存分区
     */
    deletePartition(partitionId): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('smartCachePool.deletePartition');
        rest.setParams({ id: partitionId });
        return this.httpService.delete(rest);
    }
}
